
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import MindMapFlow from '@/components/mindmap/MindMapFlow';
import { Node, Edge } from '@xyflow/react';

interface MindMapData {
  id: string;
  board_id: string;
  title: string;
  nodes: string;
  edges: string;
  created_at: string;
  updated_at: string;
  user_id: string;
}

export default function MindMapPage() {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Buscar mapa mental existente
  const { data: mindMap, isLoading } = useQuery({
    queryKey: ['mindmap', boardId],
    queryFn: async (): Promise<MindMapData | null> => {
      if (!boardId) throw new Error('Board ID não encontrado');
      
      const { data, error } = await supabase
        .from('mind_maps')
        .select('*')
        .eq('board_id', boardId)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!boardId
  });

  // Buscar informações do quadro
  const { data: board } = useQuery({
    queryKey: ['board', boardId],
    queryFn: async () => {
      if (!boardId) throw new Error('Board ID não encontrado');
      
      const { data, error } = await supabase
        .from('kanban_boards')
        .select('name')
        .eq('id', boardId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!boardId
  });

  // Mutation para salvar mapa mental
  const saveMindMapMutation = useMutation({
    mutationFn: async ({ nodes, edges, title }: { nodes: Node[], edges: Edge[], title: string }) => {
      if (!boardId) throw new Error('Board ID não encontrado');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const mindMapData = {
        board_id: boardId,
        title,
        nodes: JSON.stringify(nodes),
        edges: JSON.stringify(edges),
        user_id: user.id,
        updated_at: new Date().toISOString()
      };

      if (mindMap) {
        // Atualizar existente
        const { data, error } = await supabase
          .from('mind_maps')
          .update(mindMapData)
          .eq('id', mindMap.id)
          .select()
          .single();
        
        if (error) throw error;
        return data;
      } else {
        // Criar novo
        const { data, error } = await supabase
          .from('mind_maps')
          .insert(mindMapData)
          .select()
          .single();
        
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mindmap', boardId] });
    },
    onError: (error) => {
      console.error('Erro ao salvar mapa mental:', error);
      toast.error('Erro ao salvar mapa mental');
    }
  });

  const handleSave = (nodes: Node[], edges: Edge[], title: string) => {
    saveMindMapMutation.mutate({ nodes, edges, title });
  };

  const handleGoBack = () => {
    navigate('/workflow');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Preparar dados iniciais se existir mapa salvo
  const initialNodes: Node[] = mindMap && mindMap.nodes ? JSON.parse(mindMap.nodes) : [];
  const initialEdges: Edge[] = mindMap && mindMap.edges ? JSON.parse(mindMap.edges) : [];
  const initialTitle = mindMap ? mindMap.title : '';

  return (
    <div className="h-screen flex flex-col">
      <div className="bg-white border-b px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={handleGoBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <h1 className="text-lg font-semibold">
            Mapa Mental - {board?.name || 'Carregando...'}
          </h1>
        </div>
      </div>

      {boardId && (
        <MindMapFlow
          boardId={boardId}
          onSave={handleSave}
          initialNodes={initialNodes}
          initialEdges={initialEdges}
          initialTitle={initialTitle}
        />
      )}
    </div>
  );
}
